import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, CheckCircle2 } from 'lucide-react';
import { caseStudies } from '../caseStudiesData';
import SEO from '../components/SEO';
import AnimatedSection from '../components/AnimatedSection';
import LoadingSpinner from '../components/LoadingSpinner';
import { useI18n } from '../i18n/I18nContext';

function CaseStudyPage() {
  const { t, localizedPath } = useI18n();
  const [isLoading, setIsLoading] = React.useState(true);
  const { id } = useParams<{ id: string }>();
  const caseStudy = caseStudies.find(study => study.id === id);

  React.useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!caseStudy) {
    return (
      <div className="container mx-auto px-4 py-20">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl font-bold mb-8">{t.caseStudy.notFound}</h1>
          <Link to={localizedPath('/')} className="text-[#FDCB12] hover:text-[#FF5500] transition-colors">
            {t.caseStudy.returnHome}
          </Link>
        </div>
      </div>
    );
  }

  const translated = t.caseStudy.items[caseStudy.id as keyof typeof t.caseStudy.items];

  return (
    <div className="container mx-auto px-4 py-20">
      <div className="max-w-4xl mx-auto">
        <SEO
          title={translated?.title ?? caseStudy.title}
          description={translated?.description ?? caseStudy.description}
          image={caseStudy.image}
          type="article"
        />
        <AnimatedSection animation="fade-in-up">
          <Link
            to={localizedPath('/portfolio')}
            className="inline-flex items-center text-[#FDCB12] hover:text-[#FF5500] transition-colors mb-8"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            {t.caseStudy.backToPortfolio}
          </Link>

          <div className="relative h-[400px] rounded-xl overflow-hidden mb-12">
            <img
              src={caseStudy.image}
              alt={translated?.title ?? caseStudy.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
            <div className="absolute bottom-0 left-0 p-8">
              <span className="bg-[#FDCB12] text-black px-4 py-1 rounded-full text-sm mb-4 inline-block">
                {translated?.category ?? caseStudy.category}
              </span>
              <h1 className="text-4xl font-bold text-white mb-2">{translated?.title ?? caseStudy.title}</h1>
              <p className="text-gray-300">{t.caseStudy.client} {translated?.client ?? caseStudy.client}</p>
            </div>
          </div>
        </AnimatedSection>

        <AnimatedSection animation="fade-in-up" delay={200}>
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div>
              <h2 className="text-2xl font-bold mb-4">{t.caseStudy.challenge}</h2>
              <p className="text-gray-300">{translated?.challenge ?? caseStudy.challenge}</p>
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-4">{t.caseStudy.solution}</h2>
              <p className="text-gray-300">{translated?.solution ?? caseStudy.solution}</p>
            </div>
          </div>
        </AnimatedSection>

        <AnimatedSection animation="fade-in-up" delay={300}>
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-6">{t.caseStudy.keyResults}</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {(translated?.results ?? caseStudy.results).map((result, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 bg-zinc-900 p-4 rounded-lg border border-zinc-800"
                >
                  <CheckCircle2 className="h-6 w-6 text-[#FDCB12] flex-shrink-0" />
                  <p className="text-gray-300">{result}</p>
                </div>
              ))}
            </div>
          </div>
        </AnimatedSection>

        <AnimatedSection animation="fade-in-up" delay={400}>
          <div className="mb-12">
            <h2 className="text-2xl font-bold mb-6">{t.caseStudy.technologiesUsed}</h2>
            <div className="flex flex-wrap gap-3">
              {caseStudy.technologies.map((tech, index) => (
                <span
                  key={index}
                  className="bg-zinc-900 text-gray-300 px-4 py-2 rounded-full border border-zinc-800"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </AnimatedSection>

        {caseStudy.projectUrl && (
          <AnimatedSection animation="fade-in-up" delay={500}>
            <div className="text-center">
              <a
                href={caseStudy.projectUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-[#FDCB12] text-black px-6 py-3 rounded-md hover:bg-[#FF5500] hover:text-white transition-all duration-300"
              >
                {t.caseStudy.visitProject}
                <ExternalLink className="ml-2 h-5 w-5" />
              </a>
            </div>
          </AnimatedSection>
        )}
      </div>
    </div>
  );
}

export default CaseStudyPage;
